import { z } from 'zod';
import * as yup from 'yup';
import { TasksListFilter } from './filters';

export const ScheduleSchema = z.object({
  externalId: z.string(),
  resourceId: z.string().uuid(),
  tenantId: z.string(),
  workingDayStartTime: z.string(),
  workingDayEndTime: z.string(),
  timeZoneId: z.string(),
  dailyClaimOwnerTasksCount: z.number().optional(),
  isVisible: z.boolean(),
});

export type Schedule = z.infer<typeof ScheduleSchema>;

export interface RecurrentIntervals {
  recurrentStartDate: string;
  recurrentEndDate: string;
  isWorking: boolean;
  name: string;
}

export interface NonRecurrentIntervals {
  startDate: string;
  endDate: string;
  isWorking: boolean;
}

type Interval = NonRecurrentIntervals[] | RecurrentIntervals[] | [];
export interface Calendars {
  id: string;
  name: string;
  unspecifiedTimeIsWorking: boolean;
  intervals: Interval[];
  iconCls?: string;
  cls?: string;
}
export interface EditScheduleForm {
  startDateTime: Date;
  endDateTime: Date;
  repeat: boolean;
  repeatFor: number;
  days: DaysForm;
  timeZoneId: string;
  externalId?: string;
  dailyClaimOwnerTasksCount?: number;
}

export interface DaysForm {
  sunday: boolean;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
}

export const editScheduleFormDefault: EditScheduleForm = {
  startDateTime: new Date(),
  endDateTime: new Date(),
  repeatFor: 0,
  repeat: false,
  timeZoneId: '',
  days: {
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
  },
};

export const editScheduleFormSchema = yup.object().shape({
  startDateTime: yup
    .date()
    .test(
      'less_than_24_hours',
      'Schedule Cannot Exceed 24 Hours',
      (value, context) => {
        const { endDateTime } = context.parent;
        if (!endDateTime || !value) {
          return false;
        }

        return (
          (endDateTime.getTime() - value.getTime()) / (60 * 60 * 1000) <= 24
        );
      }
    )
    .required(),
  endDateTime: yup
    .date()
    .test(
      'min_end_date',
      'End date time must be after start date time',
      (value, context) => {
        const { startDateTime } = context.parent;
        if (!startDateTime || !value) {
          return false;
        }

        return value > startDateTime;
      }
    )
    .required('End Date Time is required'),
  repeat: yup.boolean(),
  repeatFor: yup.number().when('repeat', {
    is: true,
    then: yup
      .number()
      .min(1, 'Repeated Days Must Be Greater Than 0')
      .max(60, 'Repeated Days Must Be Less Than or Equal to 60')
      .required('Number of days required')
      .transform((value) => (value ? value : 0)),
  }),
  timeZoneId: yup.string().required('Time zone is required'),
  days: yup
    .object()
    .shape({
      sunday: yup.boolean(),
      monday: yup.boolean(),
      tuesday: yup.boolean(),
      wednesday: yup.boolean(),
      thursday: yup.boolean(),
      friday: yup.boolean(),
      saturday: yup.boolean(),
    })
    .when('repeat', {
      is: true,
      then: yup
        .object()
        .shape({
          sunday: yup.boolean(),
          monday: yup.boolean(),
          tuesday: yup.boolean(),
          wednesday: yup.boolean(),
          thursday: yup.boolean(),
          friday: yup.boolean(),
          saturday: yup.boolean(),
        })
        .test(
          'day_selected',
          'A repeated day must be selected',
          (value) =>
            !!value.sunday ||
            !!value.monday ||
            !!value.tuesday ||
            !!value.wednesday ||
            !!value.thursday ||
            !!value.friday ||
            !!value.saturday
        ),
    }),
});

export type ScheduleFilters = TasksListFilter & {
  scheduleStartDate: string;
  scheduleEndDate: string;
  isActive?: boolean;
  pageSize: number;
};
